import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight } from "lucide-react";

const AUTHORS = {
  cody: { name: "Cody D. Thrash", role: "Co-founder · Writer" },
  samantha: { name: "Samantha N. Thrash", role: "Co-founder · Writer" },
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const author = params.get("author") || "all";

  useEffect(() => {
    setLoading(true);
    const q = author !== "all" ? `?author=${author}` : "";
    api.get(`/blog${q}`).then(({ data }) => { setPosts(data); setLoading(false); });
  }, [author]);

  const setFilter = (a) => {
    if (a === "all") setParams({}); else setParams({ author: a });
  };

  return (
    <div data-testid="blog-page" className="max-w-7xl mx-auto px-6 lg:px-12 py-10 md:py-20">
      <div className="max-w-4xl mb-12">
        <div className="overline mb-4">Journal</div>
        <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-8xl tracking-[-0.04em] leading-[0.95] md:leading-[0.9]" style={{ color: "var(--ink)" }}>
          The <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>Disciple Life</span> journal.
        </h1>
        <p className="mt-4 md:mt-8 text-base md:text-lg leading-relaxed max-w-2xl" style={{ color: "var(--ink-2)" }}>
          Long-form essays, reflections, and field notes from <strong style={{ color: "var(--ink)" }}>Cody</strong> and <strong style={{ color: "var(--ink)" }}>Samantha</strong>. Written separately. Read together.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-12 border-b pb-4" style={{ borderColor: "var(--border-subtle)" }}>
        {[
          { k: "all", label: "All writing" },
          { k: "cody", label: "By Cody" },
          { k: "samantha", label: "By Samantha" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            data-testid={`blog-filter-${t.k}`}
            className="px-4 py-2 text-sm tracking-wider uppercase transition-colors"
            style={{
              color: author === t.k ? "var(--ember)" : "var(--ink-2)",
              borderBottom: author === t.k ? "2px solid var(--ember)" : "2px solid transparent",
              marginBottom: "-1rem",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: "var(--muted)" }}>Loading…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {posts.map((p, i) => (
            <Link key={p.id} to={`/blog/${p.id}`} data-testid={`blog-post-${p.id}`} className="group block">
              {p.cover_url && (
                <div className="aspect-[16/10] overflow-hidden mb-6">
                  <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                </div>
              )}
              <div className="flex items-center gap-3 mb-3 text-xs uppercase tracking-[0.2em]">
                <span style={{ color: "var(--ember)" }}>{AUTHORS[p.author]?.name || p.author}</span>
                <span className="w-6 h-px" style={{ background: "var(--border-strong)" }} />
                <span style={{ color: "var(--muted)" }}>
                  {new Date(p.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </div>
              <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight leading-[1] group-hover:text-[var(--ember)] transition-colors" style={{ color: "var(--ink)" }}>
                {p.title}
              </h2>
              <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--ink-2)" }}>
                {p.excerpt}
              </p>
              <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]" style={{ color: "var(--ember)" }}>
                Read essay <ArrowRight size={12} />
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
